import { TabProps } from '@mui/material';
import { DateTime } from 'luxon';
import { Location } from 'react-router';
import { FormikContextType } from 'formik';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import {
    IUserExtraFieldFragment,
    IExtraTrainingFieldFragment,
    IUserExtraChoiceField,
    ITrainingExtraChoiceField,
    IUserExtraStringField,
    ITrainingExtraStringField,
    ICustomFontFragment,
    IFontSize,
    IFontWeight,
    IExtraCategoryFilterInput,
    IContentTypeValue,
    IRole,
    IExtraCategoryLabelFragment,
    IExtraCategoryValueFragment,
} from 'graphql/types';
import { IRouteLinkProps } from 'route/components/RouteLink';

import { TPaginationSettings } from './hooks/usePagination';

export enum EGraphQLError {
    AuthNotLoggedIn = 'AuthNotLoggedIn',
    AuthTwoFactorNotVerified = 'AuthTwoFactorNotVerified',
    AuthTwoFactorNotSetup = 'AuthTwoFactorNotSetup',
    TrialExpired = 'TrialExpired',
}

export type TTabsMenuItem = IRouteLinkProps & TabProps;

export type TFileUploadStatus = {
    done: boolean;
    success: boolean;
    progress: number;
    fileUrl?: string;
    errorMessage?: string;
};

export interface IFilterValue {
    id?: string;
    name: string;
}

export type TStringFilterValue = string;
export type TDateFilterValue = string | DateTime;
export type TObjectFilterValue = { id: string; name: string };
export type TRangeFilterValue = [from: number, to: number];

export type TFilterValue =
    | TStringFilterValue
    | TDateFilterValue
    | TObjectFilterValue
    | TRangeFilterValue
    | null
    | undefined;

export type TFilterFormValue = TFilterValue | TFilterValue[];
export type TFilterFormValues = Record<string, TFilterFormValue>;

export enum EFilterType {
    String = 'string',
    Date = 'date',
    Object = 'object',
    Range = 'range',
    Radio = 'radio',
}

export interface IFilterFormItem<
    T extends TFilterFormValue = TFilterFormValue,
    C extends React.ElementType<IFilterFormItem> = React.ElementType,
> {
    key: string;
    label: string;
    defaultExpanded?: boolean;
    permissionRoles?: IRole[];
    component?: C;
    componentProps?: React.ComponentProps<C>;
    initialValue?: TFilterFormValue | string | null;
    /** Show a clear link in the section header that resets this filter's fields. */
    clearable?: boolean;
    /** Reduce the section's vertical padding so the field and section read as one. */
    dense?: boolean;
    fields: IFilterFormField<T>[];
    onChangeField?: (
        name: string,
        value: TFilterFormValue,
        field: IFilterFormField<T>,
        formikContext: FormikContextType<TFilterFormValues>
    ) => void;
}

export type TParseQueryFn<T extends TFilterFormValue = TFilterFormValue> = <R>(
    value: T
) => R | unknown;

export interface IFilterFormField<
    T extends TFilterFormValue = TFilterFormValue,
    C extends React.ElementType = React.ElementType,
> {
    name: string;
    label?: string;
    type: EFilterType;
    initialValue?: T | null;
    component?: C;
    componentProps?: React.ComponentProps<C>;
    /** Function to parse the filter value before applying it as filter value */
    parseFilterValue?: (value: T) => T;
    /** Custom key to use for the query, if not set the `name` will be used */
    queryKey?: string;
    /** Function to parse the filter value before using it for the query */
    parseQueryValue?: TParseQueryFn<T>;
    onChange?: (
        value: unknown,
        formikContext: FormikContextType<TFilterFormValues>
    ) => void;
    chip?: {
        label?: string;
        parseValue?: (value: T) => T;
        appendValue?: string;
    };
}

export type TFilterBarItem = {
    name: string;
    value: string;
    children?: TFilterBarItem[];
    parent?: string;
    parentChipName?: string;
    chipName?: string;
    singleValue?: boolean;
    displayName?: string;
    urlParamName?: string;
    urlParentParamName?: string;
    extraCategoryData?: IExtraCategoryFilterInput;
    onDelete?(): void;
};

export interface IFilterListItem {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface IFilterListProps {
    items: IFilterListItem[];
    /** Selected item values. */
    value: string[];
    onChange: (value: string[]) => void;
    'aria-label'?: string;
}

export type TExtraField = IUserExtraFieldFragment | IExtraTrainingFieldFragment;

export type TExtraChoiceField =
    | IUserExtraChoiceField
    | ITrainingExtraChoiceField;

export type TExtraStringField =
    | IUserExtraStringField
    | ITrainingExtraStringField;

export type TDate = { startDate: Date; endDate: Date };

export interface IWebfontList {
    kind: string;
    items: IWebfontFamily[];
}

export interface IWebfontFamily {
    family: string;
    subsets: string[];
    variants: string[];
    files: { [variant: string]: string };
}

export enum IFontFamilyGroup {
    CustomFonts = 'customFonts',
    BasicFonts = 'basicFonts',
}

export type TFontFamilyOption = {
    label: IWebfontFamily['family'] | ICustomFontFragment['family'];
    value: IWebfontFamily['family'] | ICustomFontFragment['family'];
    group: string;
};

// This fontWeight is used for loading the fonts
export enum ELoadFontWeight {
    Thin = '100',
    ExtraLight = '200',
    Light = '300',
    Normal = '400',
    Medium = '500',
    SemiBold = '600',
    Bold = '700',
    ExtraBold = '800',
    Black = '900',
}

export enum EFontStyle {
    Normal = 'normal',
    Italic = 'italic',
}

export interface IPluvoFontFamily {
    name: string;
    styles: (keyof typeof ELoadFontWeight)[];
}

export interface ILoadFont {
    family: string;
    weights: (ELoadFontWeight | string | number)[];
    styles?: EFontStyle[];
}

export type TFontSizeOption = { label: string; value: IFontSize };
export type TFontWeightOption = { label: string; value: IFontWeight };

export type TFontWeightOptions = { [key: string]: TFontWeightOption[] };

export type TOption = { label: React.ReactNode; value: string };

export interface IRow {
    index: number;
    isSelected: boolean;
    onSelect: () => void;
}

export type TDateRange = {
    startDate?: DateTime;
    endDate?: DateTime;
};

export type TSelectOption = {
    label: string;
    value: string;
    disabled?: boolean;
    divider?: boolean;
};

export type TTableAction = {
    tooltipTitle?: string;
    iconPath: string;
    onClick: (selectedItems: string[]) => void;
};

export interface IPaginationProps {
    paginationSettings: TPaginationSettings;
    setPage: (page: number) => void;
}

export interface ITableSelectionProps<T> {
    rows: IRow[];
    selected: string[];
    isSelected: boolean;
    isSelectedAll: boolean;
    onSelectAll: () => void;
    selectableItems: T[];
    setSelected: (selected: string[]) => void;
}

export interface ISearchProps {
    filters: TFilterBarItem[];
    searchQueryParam: string;
    selectedFilters: TFilterBarItem[];
    filtersLoading: boolean;
    handleSearch: (searchValue: string) => void;
    handleFilterSelect: (selected: TFilterBarItem[]) => void;
}

export enum IGlobalDrawerType {
    Offer = 'OFFER',
    OfferEvent = 'OFFER_EVENT',
    PortfolioItem = 'PORTFOLIO_ITEM',
    UserCreate = 'USER_CREATE',
    UserUpdate = 'USER_UPDATE',
    UserProfile = 'USER_PROFILE',
    TrainingAssignmentGrade = 'TRAINING_ASSIGNMENT_GRADE',
    TrainingAssignmentDetail = 'TRAINING_ASSIGNMENT_DETAIL',
    TrainingUpdate = 'TRAINING_UPDATE',
    PortfolioManagementSettings = 'PORTFOLIO_MANAGEMENT_SETTINGS',
    OfferManagementSettings = 'OFFER_MANAGEMENT_SETTINGS',
    Group = 'GROUP',
    SkillCreate = 'SKILL_CREATE',
    SkillUpdate = 'SKILL_UPDATE',
    ReceivedCertificate = 'RECEIVED_CERTIFICATE',
    Notifications = 'NOTIFICATIONS',
    OpenIdCreate = 'OPEN_ID_CREATE',
    OpenIdUpdate = 'OPEN_ID_UPDATE',
    LTICreate = 'LTI_CREATE',
    LTIUpdate = 'LTI_UPDATE',
    CertificateManage = 'CERTIFICATE_MANAGE',
    BudgetRequest = 'BUDGET_REQUEST',
    BudgetRequestManage = 'BUDGET_REQUEST_MANAGE',
}

export enum IGlobalDrawerActionType {
    Add = 'ADD',
    Delete = 'DELETE',
    Open = 'OPEN',
    Close = 'CLOSE',
}

export interface IGlobalDrawerAction {
    globalDrawer: IGlobalDrawer;
    type: IGlobalDrawerActionType;
}

export interface IGlobalDrawer {
    type: IGlobalDrawerType;
    actionType?: IGlobalDrawerActionType;
    open?: boolean;
    itemId?: string;
    location?: Location;
    props?: Record<string, unknown>;
}

export interface IContentQueryVariables {
    contentType: IContentTypeValue;
    objectId: string;
}

export interface IFilterPermissions {
    rolesFilter: IRole[];
}

export type TInputOption = {
    id: string;
    name: string;
    extraCategoryLabels:
        | string[]
        | IExtraCategoryLabelFragment[]
        | IExtraCategoryValueFragment[];
    isInfo: boolean;
    divider?: boolean;
};

export type TControllerProps<TFieldValues extends FieldValues> = Omit<
    UseControllerProps<TFieldValues>,
    'name'
>;

export interface IImageVariant {
    minWidth: number;
    minHeight: number;
    aspectRatio: number;
}

export enum EImageVariant {
    Header = 'header',
    Card = 'card',
    UserProfile = 'userProfile',
    Logo = 'logo',
    Favicon = 'favicon',
    DrawerHeader = 'drawerHeader',
}

export type TFileFormValue = {
    __typename: 'File';
    id: string;
    name?: string | null;
    url?: string | null;
};

export interface ICSVData {
    headers: string[];
    rows: string[][];
    hasHeaders: boolean;
}

export interface IHeaderOption<T = string> {
    label: string;
    value: T;
}

export interface IDetectHasHeadersOptions {
    knownHeaders?: Set<string>;
    dataPattern?: RegExp;
}

export interface IParseCSVOptions {
    knownHeaders?: Set<string>;
    dataPattern?: RegExp;
}

export interface IMatchHeadersOptions<T> {
    aliases?: Record<string, T>;
    ignoreValue: T;
}
